import type { ICommand } from './ICommand';
import { BaseNodeCommand, type NodeChangeRecord } from './base/BaseNodeCommand';
import type { Node } from '../models/scene';
import {
  calculateMediaDeltaFromChangeRecords,
  emitDiagramMediaReferenceDelta,
} from '../../domains/media/utils/commandMediaReferenceEvents';

export type ImageNodeUpdate = {
  nodeId: string;
  changes: Partial<Node>;
};

type MergeAnchorOldState = { nodeId: string; oldState: Partial<Node> };

function clonePartialNodeForUndoSnapshot(oldState: Partial<Node>): Partial<Node> {
  const o: Record<string, unknown> = { ...oldState };
  if (oldState.attrs && typeof oldState.attrs === 'object') {
    o.attrs = { ...(oldState.attrs as Record<string, unknown>) };
  }
  const props = (oldState as { props?: Record<string, unknown> }).props;
  if (props && typeof props === 'object') {
    const nextProps = { ...props };
    const pa = props.attrs;
    if (pa && typeof pa === 'object') {
      nextProps.attrs = { ...(pa as Record<string, unknown>) };
    }
    o.props = nextProps;
  }
  return o as Partial<Node>;
}

function mergeTwoImageNodeUpdates(prev: ImageNodeUpdate, next: ImageNodeUpdate): ImageNodeUpdate {
  const pa = (prev.changes as { attrs?: Record<string, unknown> }).attrs;
  const na = (next.changes as { attrs?: Record<string, unknown> }).attrs;
  const attrs =
    pa || na
      ? { ...(typeof pa === 'object' ? pa : {}), ...(typeof na === 'object' ? na : {}) }
      : undefined;
  const changes = { ...prev.changes, ...next.changes } as Partial<Node>;
  if (attrs) {
    (changes as { attrs?: Record<string, unknown> }).attrs = attrs;
  }
  return { nodeId: prev.nodeId, changes };
}

function mergeImageNodeUpdateLists(a: ImageNodeUpdate[], b: ImageNodeUpdate[]): ImageNodeUpdate[] {
  const map = new Map<string, ImageNodeUpdate>();
  for (const u of a) {
    map.set(u.nodeId, { nodeId: u.nodeId, changes: { ...u.changes } });
  }
  for (const u of b) {
    const prev = map.get(u.nodeId);
    if (!prev) {
      map.set(u.nodeId, { nodeId: u.nodeId, changes: { ...u.changes } });
    } else {
      map.set(u.nodeId, mergeTwoImageNodeUpdates(prev, u));
    }
  }
  return [...map.values()];
}

function sameImageUpdateNodeSet(a: ImageNodeUpdate[], b: ImageNodeUpdate[]): boolean {
  const sa = [...new Set(a.map((u) => u.nodeId))].sort().join(',');
  const sb = [...new Set(b.map((u) => u.nodeId))].sort().join(',');
  return sa.length > 0 && sa === sb;
}

export class UpdateImageNodeAttrsCommand extends BaseNodeCommand {
  private readonly updates: ImageNodeUpdate[];
  private readonly mergeAnchorOldStates: MergeAnchorOldState[] | null;

  constructor(updates: ImageNodeUpdate[], mergeAnchorOldStates: MergeAnchorOldState[] | null = null) {
    super('Update Image Node Attrs', `Apply ${updates.length} image node update(s)`);
    this.updates = updates;
    this.mergeAnchorOldStates = mergeAnchorOldStates;
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof UpdateImageNodeAttrsCommand)) return false;
    return sameImageUpdateNodeSet(this.updates, other.updates);
  }

  mergeWith(other: ICommand): UpdateImageNodeAttrsCommand {
    const incoming = other as UpdateImageNodeAttrsCommand;
    const merged = mergeImageNodeUpdateLists(this.updates, incoming.updates);
    const anchors: MergeAnchorOldState[] | null =
      this.changeRecords.length > 0
        ? this.changeRecords.map((r) => ({
            nodeId: r.nodeId,
            oldState: clonePartialNodeForUndoSnapshot(r.oldState),
          }))
        : null;
    return new UpdateImageNodeAttrsCommand(merged, anchors);
  }

  protected doExecute(): NodeChangeRecord[] {
    if (this.updates.length === 0) {
      return [];
    }

    const mutationManager = this.getMutationManager() as unknown as {
      applyNodeChange: (nodeId: string, changes: Partial<Node>) => NodeChangeRecord;
      applyBatchNodeChanges?: (updates: ImageNodeUpdate[]) => NodeChangeRecord[];
    };

    let records: NodeChangeRecord[];
    if (this.updates.length > 1 && typeof mutationManager.applyBatchNodeChanges === 'function') {
      records = mutationManager.applyBatchNodeChanges(this.updates);
    } else {
      records = [];
      for (const update of this.updates) {
        records.push(mutationManager.applyNodeChange(update.nodeId, update.changes));
      }
    }

    if (this.mergeAnchorOldStates?.length) {
      records = records.map((rec) => {
        const anchor = this.mergeAnchorOldStates!.find((a) => a.nodeId === rec.nodeId);
        if (!anchor) return rec;
        return {
          ...rec,
          oldState: clonePartialNodeForUndoSnapshot(anchor.oldState),
          newState: rec.newState,
        };
      });
    }

    return records;
  }

  override undo(): void {
    const forwardDelta = calculateMediaDeltaFromChangeRecords(this.changeRecords);
    super.undo();
    emitDiagramMediaReferenceDelta({
      addedKeys: forwardDelta.removedKeys,
      removedKeys: forwardDelta.addedKeys,
    });
  }

  override redo(): void {
    super.redo();
    const forwardDelta = calculateMediaDeltaFromChangeRecords(this.changeRecords);
    emitDiagramMediaReferenceDelta({
      addedKeys: forwardDelta.addedKeys,
      removedKeys: forwardDelta.removedKeys,
    });
  }
}
